import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-slate-400 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-semibold"
            >
              Reload Page
            </button>
            <details className="mt-4 text-left">
              <summary className="text-slate-400 cursor-pointer text-sm">Error Details</summary>
              <pre className="mt-2 text-xs text-slate-500 bg-slate-900 p-4 rounded overflow-auto">
                {this.state.error?.stack}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Log that React is mounting
console.log('React app is mounting...');
console.log('index.tsx loaded successfully');

// Get PayPal client ID from environment or use a default
const paypalClientId = import.meta.env.VITE_PAYPAL_CLIENT_ID || '';
console.log('PayPal Client ID loaded:', paypalClientId ? `${paypalClientId.substring(0, 20)}...` : 'NOT SET');

// Mount function - ensures DOM is ready
const mountApp = async () => {
  try {
    console.log('[Mount] Starting React mount process...');
    console.log('[Mount] Document ready state:', document.readyState);
    
    // Verify App import works
    try {
      console.log('[Mount] Verifying App import...');
      console.log('[Mount] React version:', React.version);
      console.log('[Mount] ReactDOM available:', !!ReactDOM);
      console.log('[Mount] App type:', typeof App);
      if (!App) {
        throw new Error('App component is undefined');
      }
      if (typeof App !== 'function') {
        throw new Error(`App is not a function, it's a ${typeof App}`);
      }
      console.log('[Mount] ✅ App import verified');
    } catch (importError) {
      console.error('[Mount] ❌ App import failed:', importError);
      throw importError;
    }
    
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      console.error('[Mount] Root element not found! Creating it...');
      const newRoot = document.createElement('div');
      newRoot.id = 'root';
      document.body.appendChild(newRoot);
      const root = ReactDOM.createRoot(newRoot);
      console.log('[Mount] Created root element and mounting React...');
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </React.StrictMode>
      );
      console.log('[Mount] ✅ React mounted successfully!');
      
      // Verify mount after a short delay
      setTimeout(() => {
        const mountedRoot = document.getElementById('root');
        if (mountedRoot && mountedRoot.children.length > 0) {
          console.log('[Mount] ✅ Verified: React content is rendered');
        } else {
          console.error('[Mount] ⚠️ Warning: React may not have rendered content');
        }
      }, 100);
    } else {
      console.log('[Mount] Root element found, clearing and mounting React...');
      // Clear any existing content
      rootElement.innerHTML = '';
      const root = ReactDOM.createRoot(rootElement);
      console.log('[Mount] Rendering React app...');
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </React.StrictMode>
      );
      console.log('[Mount] ✅ React mounted successfully!');
      
      // Verify mount after a short delay
      setTimeout(() => {
        const mountedRoot = document.getElementById('root');
        if (mountedRoot && mountedRoot.children.length > 0) {
          console.log('[Mount] ✅ Verified: React content is rendered');
        } else {
          console.error('[Mount] ⚠️ Warning: React may not have rendered content');
        }
      }, 100);
    }
  } catch (error) {
    console.error('[Mount] ❌ Failed to mount React:', error);
    console.error('[Mount] Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : undefined
    });
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0f172a; color: white; flex-direction: column; gap: 1rem; padding: 2rem;">
          <h1 style="font-size: 2rem; font-weight: bold;">React Mount Error</h1>
          <p style="color: #ef4444;">${error instanceof Error ? error.message : 'Unknown error'}</p>
          <pre style="background: #1e293b; padding: 1rem; border-radius: 0.5rem; overflow: auto; max-width: 800px; font-size: 0.875rem; text-align: left;">
            ${error instanceof Error ? error.stack : String(error)}
          </pre>
          <button onclick="window.location.reload()" style="background: #6366f1; color: white; padding: 0.75rem 1.5rem; border-radius: 0.5rem; border: none; cursor: pointer; font-weight: bold;">
            Reload Page
          </button>
        </div>
      `;
    }
    throw error; // Re-throw to be caught by outer handler
  }
};

// Immediate mount attempt - don't wait
const attemptMount = () => {
  console.log('[Mount] Attempting immediate mount...');
  mountApp().catch((error) => {
    console.error('[Mount] Fatal error during mount:', error);
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0f172a; color: white; flex-direction: column; gap: 1rem; padding: 2rem;">
          <h1 style="font-size: 2rem; font-weight: bold;">Fatal Mount Error</h1>
          <p style="color: #ef4444;">${error instanceof Error ? error.message : 'Unknown error'}</p>
          <pre style="background: #1e293b; padding: 1rem; border-radius: 0.5rem; overflow: auto; max-width: 800px; font-size: 0.875rem; text-align: left;">
            ${error instanceof Error ? error.stack : String(error)}
          </pre>
          <button onclick="window.location.reload()" style="background: #6366f1; color: white; padding: 0.75rem 1.5rem; border-radius: 0.5rem; border: none; cursor: pointer; font-weight: bold;">
            Reload Page
          </button>
        </div>
      `;
    }
  });
};

// Try to mount immediately if DOM is ready
if (document.readyState === 'loading') {
  console.log('[Mount] DOM is loading, waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[Mount] DOMContentLoaded fired, mounting...');
    attemptMount();
  });
} else {
  console.log('[Mount] DOM is ready, mounting immediately...');
  // DOM is already ready, mount immediately
  attemptMount();
}

// Fallback: try mounting after a short delay even if DOMContentLoaded didn't fire
setTimeout(() => {
  const root = document.getElementById('root');
  if (root && root.innerHTML.includes('Loading OrcaTools')) {
    console.warn('[Mount] Fallback: Root still has fallback content, attempting mount again...');
    attemptMount();
  }
}, 500);